import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  Calendar, 
  User, 
  Tag,
  ChevronRight,
  Newspaper,
  Rss
} from 'lucide-react';

const blogPosts = [
  {
    title: "Introducing NewLoop: The Future of Professional Growth",
    description: "Learn about our mission to transform how professionals connect and grow in their careers.",
    author: "Sarah Chen",
    date: "2025-03-01",
    category: "Company News",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop&q=80"
  },
  {
    title: "AI-Powered Career Development: A New Era",
    description: "How artificial intelligence is revolutionizing professional development and career growth.",
    author: "Marcus Rodriguez",
    date: "2025-02-28",
    category: "Technology",
    image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&h=400&fit=crop&q=80"
  },
  {
    title: "Building a More Inclusive Professional Network",
    description: "Our commitment to creating equal opportunities for professionals worldwide.",
    author: "Emily Taylor",
    date: "2025-02-25",
    category: "Culture",
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=400&fit=crop&q=80"
  },
  {
    title: "The Evolution of Professional Networking",
    description: "How professional networking has changed and where it's heading next.",
    author: "David Kim",
    date: "2025-02-20",
    category: "Industry Insights",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=400&fit=crop&q=80"
  },
  {
    title: "Designing for Professional Growth",
    description: "Behind the scenes of our user-centered design process.",
    author: "Lisa Johnson",
    date: "2025-02-15",
    category: "Design",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=400&fit=crop&q=80"
  },
  {
    title: "The Future of Work: Trends and Predictions",
    description: "Key trends shaping the future of work and professional development.",
    author: "Michael Chang",
    date: "2025-02-10",
    category: "Industry Insights",
    image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=400&fit=crop&q=80"
  }
];

export default function BlogPage() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background border-b">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0))]" style={{ backgroundSize: '30px 30px' }} />
        <div className="relative container px-4 md:px-6 flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] gap-4 py-12 text-center">
          <Badge variant="outline" className="mb-2">Blog</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl mb-4">
            Insights from the{' '}
            <span className="text-primary">NewLoop Team</span>
          </h1>
          <p className="max-w-[600px] text-muted-foreground md:text-xl dark:text-gray-400 mb-5">
            Latest news, insights, and updates from our team
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
            <Button asChild size="lg" className="text-base">
              <Link to="/register">
                Subscribe to Updates
                <Rss className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-base">
              <Link to="/about">
                About NewLoop
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-20 px-4 md:px-6">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Card key={index} className="overflow-hidden">
                <div className="aspect-video w-full overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary">{post.category}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <CardTitle className="line-clamp-2">{post.title}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {post.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{post.author}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="text-primary">
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-4 md:px-6 bg-primary text-primary-foreground">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
              Stay Updated
            </h2>
            <p className="mt-4 text-primary-foreground/80 md:text-xl">
              Subscribe to our newsletter for the latest insights and updates
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-base"
                asChild
              >
                <Link to="/register">
                  Subscribe Now
                  <Newspaper className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-transparent border-white hover:bg-white/10 text-base"
                asChild
              >
                <Link to="/about">
                  Learn More
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}